import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import env from './utils/env';
import SDGFilter from './components/filters/SDGFilter';
import BondsGrid from './components/bonds/BondsGrid';
import BondsList from './components/bonds/BondsList';

import '@fortawesome/fontawesome-free/css/all.min.css';

import './App.css'; // Import the CSS file
import PageInfo from './components/PageInfo';
interface Bond {
    _id: string;
    isin: string;
    update_timestamp: string;
    filename?: string[];
    report_id: string[];
    validation_id: string[];
    issue_date: string;
    maturity_date: string;
    volume: string;
    currency: string;
    duration: string;
    volume_as_number: number;
    currency_fixed: string;
    volume_has_a_range: boolean;
    volume_min_as_number: number;
    report_source_id: string[];
    use_of_proceeds: string;
    issuer: string;
    tags?: string[];
    sdg_numbers?: number[];
    public?: boolean;
}

interface PaginationMetadata {
    totalDocuments: number;
    totalPages: number;
    currentPage: number;
    pageSize: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

interface ApiResponse {
    bonds?: Bond[];
    pagination?: PaginationMetadata;
}

const BondsView: React.FC = () => {
    const { token, isAuthenticated, user, authLoaded } = useAuth();
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const apiKey = env.apiKey;
    const apiUrl = env.apiUrl;
    const limit = 50;
    const [bonds, setBonds] = useState<Bond[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [pagination, setPagination] = useState<PaginationMetadata | null>(null);
    const [, setCurrentPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [isSearching, setIsSearching] = useState<boolean>(false);
    const [, setSearchResults] = useState<Bond[]>([]);
    const [isInSearchMode, setIsInSearchMode] = useState<boolean>(false);
    const [selectedTags, setSelectedTags] = useState<string[]>([]);
    const [excludedTags, setExcludedTags] = useState<string[]>([]);
    const [showTagsFilter, setShowTagsFilter] = useState<boolean>(false);
    const [tagFilterMode, setTagFilterMode] = useState<'include' | 'exclude'>('include');
    const [showValidatedOnly, setShowValidatedOnly] = useState<boolean>(false);
    const [selectedSDGs, setSelectedSDGs] = useState<number[]>([]);
    const [viewMode, setViewMode] = useState<'grid' | 'list'>(!isAuthenticated ? 'list' : 'grid');

    // Debug logging for race condition tracking
    console.log('🔍 BondsView Render Debug:');
    console.log('   Auth loaded:', authLoaded);
    console.log('   Token exists:', !!token);
    console.log('   Token (first 20 chars):', token ? token.substring(0, 20) + '...' : 'NO TOKEN');
    console.log('   Is authenticated:', isAuthenticated);
    console.log('   User:', user ? `${user.username} (${user.role})` : 'NO USER');
    console.log('   Loading:', loading);

    // Restore state from URL parameters on mount
    useEffect(() => {
        const query = searchParams.get('search');
        const page = searchParams.get('page');
        const tags = searchParams.get('tags');
        const excludeTags = searchParams.get('excludeTags');
        const mode = searchParams.get('mode');
        const validated = searchParams.get('validated');
        const sdgs = searchParams.get('sdgs');

        console.log('🔄 Restoring state from URL:', { query, page, tags, excludeTags, mode, validated });

        if (query) {
            setSearchQuery(query);
            setIsInSearchMode(true);
        }
        if (page) {
            setCurrentPage(parseInt(page, 10));
        }
        if (tags) {
            setSelectedTags(tags.split(','));
        }
        if (excludeTags) {
            setExcludedTags(excludeTags.split(','));
        }
        if (mode) {
            setTagFilterMode(mode as 'include' | 'exclude');
        }
        if (sdgs) {
            setSelectedSDGs(sdgs.split(',').map(n => parseInt(n, 10)));
        }
        if (validated === 'true') {
            setShowValidatedOnly(true);
            console.log('   ✅ Restored validated filter from URL');
        }
    }, []); // Only run on mount

    // Update URL parameters when state changes
    const updateURLParams = (updates: {
        search?: string;
        page?: number;
        tags?: string[];
        excludeTags?: string[];
        mode?: 'include' | 'exclude';
        validated?: boolean;
        sdgs?: number[];
    }) => {
        const newParams = new URLSearchParams(searchParams);

        if (updates.search !== undefined) {
            if (updates.search) {
                newParams.set('search', updates.search);
            } else {
                newParams.delete('search');
            }
        }
        if (updates.page !== undefined) {
            newParams.set('page', updates.page.toString());
        }
        if (updates.tags !== undefined) {
            if (updates.tags.length > 0) {
                newParams.set('tags', updates.tags.join(','));
            } else {
                newParams.delete('tags');
            }
        }
        if (updates.excludeTags !== undefined) {
            if (updates.excludeTags.length > 0) {
                newParams.set('excludeTags', updates.excludeTags.join(','));
            } else {
                newParams.delete('excludeTags');
            }
        }
        if (updates.mode !== undefined) {
            newParams.set('mode', updates.mode);
        }
        if (updates.validated !== undefined) {
            if (updates.validated) {
                newParams.set('validated', 'true');
            } else {
                newParams.delete('validated');
        }
        if (updates.sdgs !== undefined) {
            if (updates.sdgs && updates.sdgs.length > 0) {
                newParams.set('sdgs', updates.sdgs.join(','));
            } else {
                newParams.delete('sdgs');
            }
            }
        }

        setSearchParams(newParams);
    };

    const fetchBonds = (page: number, search?: string, tags?: string[], excludeTags?: string[], validatedOnly?: boolean, sdgNumbers?: number[]) => {
        if (!authLoaded) {
            console.log('⏳ Skipping fetch - auth not loaded yet');
            return;
        }

        // Prevent duplicate fetches
        if (isFetching.current) {
            console.log('⏳ Skipping fetch - already fetching');
            return;
        }

        isFetching.current = true;
        setLoading(true);

        console.log('🚀 FetchBonds Starting:');
        console.log('   Page:', page);
        console.log('   Search:', search);
        console.log('   Tags:', tags);
        console.log('   Exclude Tags:', excludeTags);
        console.log('   Auth loaded:', authLoaded);
        console.log('   Token exists:', !!token);
        console.log('   Is authenticated:', isAuthenticated);

        const headers: Record<string, string> = {
            'x-api-key': `${apiKey}`
        };

        // Add authorization header if user is authenticated
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
            console.log('   ✅ Adding Authorization header');
        } else {
            console.log('   ❌ No token available - making unauthenticated request');
        }

        console.log('   📡 Making API request with headers:', {
            'x-api-key': headers['x-api-key'],
            'Authorization': headers['Authorization'] ? 'Bearer [TOKEN]' : 'NOT_SET'
        });

        // Build URL with search parameter if provided
        let url = `${apiUrl}/bonds?limit=${limit}&page=${page}`;
        if (search && search.trim()) {
            url += `&search=${encodeURIComponent(search.trim())}`;
        }

        // Build tags array for filtering
        const filterTags = tags || [];

        console.log('   🏷️  Final filter tags:', filterTags);

        // Add validated parameter if validatedOnly is true (admin only)
        if (validatedOnly !== undefined && user?.role === 'ADMIN') {
            url += `&validated=${validatedOnly}`;
            console.log('   ✅ Added validated parameter to URL:', validatedOnly);
        }

        // Add SDG filter if provided
        if (sdgNumbers && sdgNumbers.length > 0) {
            url += '&sdg_numbers=' + encodeURIComponent(sdgNumbers.join(','));
            console.log('   🌱 Added SDG numbers to URL:', sdgNumbers);
        }

        // Add tags filter if provided (admin only)
        if (filterTags.length > 0 && user?.role === 'ADMIN') {
            url += `&tags=${encodeURIComponent(filterTags.join(','))}`;
            console.log('   📡 Tags added to URL:', filterTags.join(','));
        }
        // Add exclude tags filter if provided (admin only)
        if (excludeTags && excludeTags.length > 0 && user?.role === 'ADMIN') {
            url += `&excludeTags=${encodeURIComponent(excludeTags.join(','))}`;
        }

        console.log('   🌐 Final API URL:', url);

        axios.get<ApiResponse>(url, {
            headers
        })
            .then(response => {
                console.log('   ✅ API Response received');
                console.log('   Total documents:', response.data.pagination?.totalDocuments);

                if (Array.isArray(response.data['bonds'])) {
                    setBonds(response.data['bonds']);
                    console.log('   📊 Bonds loaded:', response.data['bonds'].length);
                } else {
                    console.error('   ❌ Bonds is not an array:', response.data['bonds']);
                    setBonds([]);
                }

                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }

                setLoading(false);
                isFetching.current = false; // Reset fetching flag
            })
            .catch(error => {
                console.error('   ❌ Error fetching data:', error);
                setLoading(false);
                isFetching.current = false; // Reset fetching flag on error too
            });
    };

    // Handle page change
    const handlePageChange = (newPage: number) => {
        setCurrentPage(newPage);
        updateURLParams({ page: newPage });
        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
        if (isInSearchMode && searchQuery) {
            fetchBonds(newPage, searchQuery, includeTags, excludeTags, showValidatedOnly, selectedSDGs);
        } else {
            fetchBonds(newPage, undefined, includeTags, excludeTags, showValidatedOnly, selectedSDGs);
        }
    };

    // Perform search
    const performSearch = (page: number = 1) => {
        if (!authLoaded || !searchQuery.trim()) return;

        console.log('🔎 Performing search:', searchQuery);
        setIsSearching(true);
        setIsInSearchMode(true);
        setCurrentPage(page);

        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];

        // Update URL params first
        updateURLParams({
            search: searchQuery,
            page: page,
            tags: includeTags,
            excludeTags: excludeTags,
            mode: tagFilterMode,
            validated: showValidatedOnly,
            sdgs: selectedSDGs
        });

        // Delay fetch slightly to ensure state is settled
        setTimeout(() => {
            // Use the fetchBonds function with search parameter
            fetchBonds(page, searchQuery, includeTags, excludeTags, showValidatedOnly, selectedSDGs);

            // Reset search state after a brief delay
            setTimeout(() => {
                setIsSearching(false);
            }, 200);
        }, 50);
    };

    // Handle search form submission
    const handleSearchSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            performSearch(1);
        } else {
            clearSearch();
        }
    };

    // Clear search and return to normal view
    const clearSearch = () => {
        setSearchQuery('');
        setIsInSearchMode(false);
        setSearchResults([]);
        setCurrentPage(1);
        updateURLParams({ search: '', page: 1 });
        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
        fetchBonds(1, undefined, includeTags, excludeTags, showValidatedOnly, selectedSDGs);
    };

    // Check if user has search access
    const hasSearchAccess = () => {
        return isAuthenticated && user && ['ADMIN', 'USER', 'FREE', 'TRIAL'].includes(user.role);
    };

    // Handle tag selection
    const handleTagToggle = (tag: string) => {
        if (tagFilterMode === 'include') {
            setSelectedTags(prev => {
                const newTags = prev.includes(tag)
                    ? prev.filter(t => t !== tag)
                    : [...prev, tag];
                return newTags;
            });
        } else {
            setExcludedTags(prev => {
                const newTags = prev.includes(tag)
                    ? prev.filter(t => t !== tag)
                    : [...prev, tag];
                return newTags;
            });
        }
    };

    // Apply tags filter
    const applyTagsFilter = () => {
        setCurrentPage(1);
        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
        updateURLParams({
            page: 1,
            tags: includeTags,
            excludeTags: excludeTags,
            mode: tagFilterMode,
            search: isInSearchMode ? searchQuery : undefined
        });
        if (isInSearchMode && searchQuery) {
            fetchBonds(1, searchQuery, includeTags, excludeTags, showValidatedOnly, selectedSDGs);
        } else {
            fetchBonds(1, undefined, includeTags, excludeTags, showValidatedOnly, selectedSDGs);
        }
    };

    // Clear tags filter
    const clearTagsFilter = () => {
        setSelectedTags([]);
        setExcludedTags([]);
        setCurrentPage(1);
        updateURLParams({
            page: 1,
            tags: [],
            excludeTags: [],
            search: isInSearchMode ? searchQuery : undefined
        });
        if (isInSearchMode && searchQuery) {
            fetchBonds(1, searchQuery, [], [], showValidatedOnly, selectedSDGs);
        } else {
            fetchBonds(1, undefined, [], [], showValidatedOnly, selectedSDGs);
        }
    };

    // Toggle validated bonds filter
    const toggleValidatedFilter = () => {
        const newValidatedState = !showValidatedOnly;
        console.log('🔍 Validated filter toggled:', {
            newState: newValidatedState,
            userRole: user?.role,
            tagFilterMode,
            selectedTags,
            excludedTags,
            isInSearchMode,
            searchQuery
        });
        setShowValidatedOnly(newValidatedState);
        setCurrentPage(1);
        // Preserve search parameter in URL
        updateURLParams({
            validated: newValidatedState,
            page: 1,
            search: isInSearchMode ? searchQuery : undefined
        });
        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
        if (isInSearchMode && searchQuery) {
            fetchBonds(1, searchQuery, includeTags, excludeTags, newValidatedState, selectedSDGs);
        } else {
            fetchBonds(1, undefined, includeTags, excludeTags, newValidatedState, selectedSDGs);
        }
    };

    // Track if initial load has been done
    const initialLoadDone = useRef(false);
    // Add a ref to track if we're currently fetching to prevent duplicate calls
    const isFetching = useRef(false);

    // Only fetch on initial mount when auth is ready
    useEffect(() => {
        if (authLoaded && !initialLoadDone.current) {
            console.log('📋 Initial load - fetching bonds...');
            initialLoadDone.current = true;

            // Use setTimeout to ensure URL params from mount effect are applied
            setTimeout(() => {
                const query = searchParams.get('search');
                const page = searchParams.get('page');
                const tags = searchParams.get('tags');
                const excludeTags = searchParams.get('excludeTags');
                const validated = searchParams.get('validated');
                const sdgs = searchParams.get('sdgs');

                const pageNum = page ? parseInt(page, 10) : 1;
                const includeTagsArray = tags ? tags.split(',') : [];
                const excludeTagsArray = excludeTags ? excludeTags.split(',') : [];
                const validatedFlag = validated === 'true';
                const sdgNumbers = sdgs ? sdgs.split(',').map(n => parseInt(n, 10)) : [];

                // Determine if we're in search mode based on URL
                if (query) {
                    fetchBonds(pageNum, query, includeTagsArray, excludeTagsArray, validatedFlag, sdgNumbers);
                } else {
                    fetchBonds(pageNum, undefined, includeTagsArray, excludeTagsArray, validatedFlag, sdgNumbers);
                }
            }, 100);
        }
    }, [authLoaded]); // Only depend on authLoaded

    // Show loading while auth is loading or data is loading
    if (!authLoaded || loading) {
        return (
            <div className="loading-container">
                <div className="bonds-loader-flex">
                    <i className="fas fa-spinner fa-spin loader-icon" aria-label="Loading" />
                    <span className="loader-text">
                        {!authLoaded ? t('loading.authentication') : t('loading.bonds')}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="table-container">
            {/* View Mode Toggle - Always visible */}
            <div className="bonds-view-toggle-section" style={{ marginBottom: '15px' }}>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                        onClick={() => setViewMode('list')}
                        className={viewMode === 'list' ? 'bonds-view-active-button' : 'bonds-view-inactive-button'}
                        style={{ padding: '8px 16px' }}
                    >
                        <i className="fas fa-list" style={{ marginRight: '5px' }}></i>
                        {t('bonds.listView', 'List')}
                    </button>
                    <button
                        onClick={() => setViewMode('grid')}
                        className={viewMode === 'grid' ? 'bonds-view-active-button' : 'bonds-view-inactive-button'}
                        style={{ padding: '8px 16px' }}
                    >
                        <i className="fas fa-th" style={{ marginRight: '5px' }}></i>
                        {t('bonds.gridView', 'Grid')}
                    </button>
                </div>
            </div>

            {/* Search Section */}
            {hasSearchAccess() && (
                <div className="bonds-search-section">
                    <form onSubmit={handleSearchSubmit} className="bonds-search-form">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder={t('bonds.searchPlaceholder') + ' or ObjectId'}
                            className="bonds-search-input"
                            disabled={isSearching}
                            data-testid="bonds-search-input"
                        />
                        <button
                            type="submit"
                            disabled={isSearching || !searchQuery.trim()}
                            className="bonds-search-button"
                            data-testid="bonds-search-button"
                        >
                            {isSearching ? (
                                <>
                                    <i className="fas fa-spinner fa-spin" style={{ marginRight: '5px' }}></i>
                                    {t('loading.searching')}
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-search" style={{ marginRight: '5px' }}></i>
                                    {t('buttons.search')}
                                </>
                            )}
                        </button>
                        {isInSearchMode && (
                            <button
                                type="button"
                                onClick={clearSearch}
                                className="bonds-clear-button"
                            >
                                <i className="fas fa-times" style={{ marginRight: '5px' }}></i>
                                {t('buttons.clear')}
                            </button>
                        )}
                    </form>
                    {isInSearchMode && (
                        <div className="bonds-search-info">
                            {t('bonds.searchingFor')}: "{searchQuery}"
                        </div>
                    )}
                </div>
            )}

            {/* Quick Filters Section - Admin Only */}
            {user?.role === 'ADMIN' && (
                <div className="bonds-quick-filters-section" style={{ marginBottom: '15px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <strong>{t('bonds.quickFilters')}:</strong>
                        <button
                            onClick={toggleValidatedFilter}
                            className={showValidatedOnly ? 'bonds-apply-tags-button' : 'bonds-clear-tags-button'}
                            style={{ padding: '8px 16px' }}
                        >
                            <i className={`fas fa-${showValidatedOnly ? 'check-circle' : 'circle'}`} style={{ marginRight: '5px' }}></i>
                            {showValidatedOnly ? t('bonds.showAll') : t('bonds.showValidatedOnly')}
                        </button>
                    </div>
                </div>
            )}


            {/* SDG Filter Section - Only for authenticated users */}
            {isAuthenticated && (
                <div style={{ marginBottom: '15px' }}>
                    <SDGFilter
                    selectedSDGs={selectedSDGs}
                    onSelectionChange={(sdgs) => {
                        console.log('🌱 SDG Filter changed:', sdgs);
                        setSelectedSDGs(sdgs);
                        setCurrentPage(1);
                        // Update URL first
                        updateURLParams({
                            sdgs,
                            page: 1,
                            search: isInSearchMode ? searchQuery : undefined
                        });
                        // Use setTimeout to debounce and ensure state updates are complete
                        setTimeout(() => {
                            const includeTags = tagFilterMode === 'include' ? selectedTags : [];
                            const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
                            if (isInSearchMode && searchQuery) {
                                fetchBonds(1, searchQuery, includeTags, excludeTags, showValidatedOnly, sdgs);
                            } else {
                                fetchBonds(1, undefined, includeTags, excludeTags, showValidatedOnly, sdgs);
                            }
                        }, 50); // Small delay to prevent race conditions
                    }}
                    disabled={loading || isSearching}
                />
                </div>
            )}
            {/* Tags Filter Section - Admin Only */}
            {user?.role === 'ADMIN' && (
                <div className="bonds-tags-section">
                    <button
                        onClick={() => setShowTagsFilter(!showTagsFilter)}
                        className="bonds-tags-toggle-button"
                    >
                        <i className={`fas fa-${showTagsFilter ? 'chevron-up' : 'chevron-down'}`}></i>
                        {t('bonds.tagsFilter')} (Admin Only)
                        {(selectedTags.length > 0 || excludedTags.length > 0) &&
                            ` (${tagFilterMode === 'include' ? selectedTags.length : excludedTags.length} ${tagFilterMode === 'include' ? 'included' : 'excluded'})`}
                    </button>

                    {showTagsFilter && (
                        <div className="bonds-tags-filter-content">
                            <div className="bonds-tags-header">
                                <strong>{t('bonds.selectTags')}</strong>
                                <div style={{ marginTop: '10px', marginBottom: '10px' }}>
                                    <label style={{ marginRight: '15px' }}>
                                        <input
                                            type="radio"
                                            name="tagFilterMode"
                                            value="include"
                                            checked={tagFilterMode === 'include'}
                                            onChange={() => setTagFilterMode('include')}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Include bonds WITH these tags
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="tagFilterMode"
                                            value="exclude"
                                            checked={tagFilterMode === 'exclude'}
                                            onChange={() => setTagFilterMode('exclude')}
                                            style={{ marginRight: '5px' }}
                                        />
                                        Exclude bonds WITH these tags
                                    </label>
                                </div>
                            </div>
                            <div className="bonds-tags-checkboxes">
                                {['NO_GROUND_TRUTH', 'INVALID_ISIN', 'MISSING_DATA', 'DUPLICATE', 'VERIFIED', 'NEEDS_REVIEW', 'GREEN'].map(tag => (
                                    <label key={tag} className="bonds-tag-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={tagFilterMode === 'include' ? selectedTags.includes(tag) : excludedTags.includes(tag)}
                                            onChange={() => handleTagToggle(tag)}
                                        />
                                        <span>{tag.replace(/_/g, ' ')}</span>
                                    </label>
                                ))}
                            </div>
                            <div className="bonds-tags-actions">
                                <button
                                    onClick={applyTagsFilter}
                                    className="bonds-apply-tags-button"
                                    disabled={tagFilterMode === 'include' ? selectedTags.length === 0 : excludedTags.length === 0}
                                >
                                    <i className="fas fa-filter"></i>
                                    {t('buttons.applyFilter')}
                                </button>
                                <button
                                    onClick={clearTagsFilter}
                                    className="bonds-clear-tags-button"
                                    disabled={selectedTags.length === 0 && excludedTags.length === 0}
                                >
                                    <i className="fas fa-times"></i>
                                    {t('buttons.clearFilter')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Pagination Information */}
            {pagination && (
                <div className="pagination-container">
                    <PageInfo
                        count={bonds.length}
                        total={pagination.totalDocuments}
                        label={t('bonds.label')}
                        showPageInfo={false}
                        extra={isAuthenticated && user ? `(${t('user.loggedInAs')} ${user.role})` : undefined}
                    />
                    {/* Pagination Controls */}
                    <div className="pagination-controls">
                        <button
                            onClick={() => handlePageChange(1)}
                            disabled={!pagination.hasPreviousPage}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-double-left"></i>
                        </button>

                        <button
                            onClick={() => handlePageChange(pagination.currentPage - 1)}
                            disabled={!pagination.hasPreviousPage}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-left"></i>
                        </button>

                        <span className="pagination-page-info">Page {pagination.currentPage} of {pagination.totalPages}</span>

                        <button
                            onClick={() => handlePageChange(pagination.currentPage + 1)}
                            disabled={!pagination.hasNextPage}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-right"></i>
                        </button>

                        <button
                            onClick={() => handlePageChange(pagination.totalPages)}
                            disabled={!pagination.hasNextPage}
                            className="pagination-button"
                        >
                            <i className="fas fa-angle-double-right"></i>
                        </button>
                    </div>
                </div>
            )}
            
            {/* Bonds Display - Grid or List based on view mode */}
            {viewMode === 'list' ? (
                <BondsList
                    bonds={bonds.map(bond => ({
                        _id: bond._id,
                        id_issuer: bond.report_id?.[0] || bond._id,
                        isin: bond.isin,
                        issuer_name: bond.issuer,
                        title: bond.use_of_proceeds,
                        issue_date: bond.issue_date,
                        maturity_date: bond.maturity_date,
                        currency: bond.currency || bond.currency_fixed || 'EUR',
                        volume_issued: bond.volume_as_number,
                        coupon_rate: undefined, // Not available in current data
                        green_bond: bond.tags?.includes('green'),
                        social_bond: bond.tags?.includes('social'),
                        sustainability_bond: bond.tags?.includes('sustainability'),
                        sustainability_linked_bond: bond.tags?.includes('sustainability-linked'),
                        sdg_numbers: bond.sdg_numbers || [],
                        tags: bond.tags || [],
                        public: bond.public,
                        update_timestamp: bond.update_timestamp
                    }))}
                    loading={loading || isSearching}
                    onTagClick={isAuthenticated ? (tag) => {
                        // Add tag to filters
                        if (tagFilterMode === 'include') {
                            setSelectedTags([...selectedTags, tag]);
                        } else {
                            setExcludedTags([...excludedTags, tag]);
                        }
                    } : undefined}
                    onSDGClick={isAuthenticated ? (sdgNumber) => {
                        // Add SDG to filter
                        const newSDGs = selectedSDGs.includes(sdgNumber)
                            ? selectedSDGs.filter(n => n !== sdgNumber)
                            : [...selectedSDGs, sdgNumber].sort((a, b) => a - b);
                        setSelectedSDGs(newSDGs);
                        updateURLParams({ sdgs: newSDGs, page: 1 });
                        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
                        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
                        if (isInSearchMode && searchQuery) {
                            fetchBonds(1, searchQuery, includeTags, excludeTags, showValidatedOnly, newSDGs);
                        } else {
                            fetchBonds(1, undefined, includeTags, excludeTags, showValidatedOnly, newSDGs);
                        }
                    } : undefined}
                    emptyMessage={isInSearchMode ?
                        t('bonds.noSearchResults', 'No bonds found matching your search.') :
                        t('bonds.noBondsAvailable', 'No bonds available.')
                    }
                />
            ) : (
                <BondsGrid
                    bonds={bonds.map(bond => ({
                        _id: bond._id,
                        id_issuer: bond.report_id?.[0] || bond._id,
                        isin: bond.isin,
                        issuer_name: bond.issuer,
                        title: bond.use_of_proceeds,
                        issue_date: bond.issue_date,
                        maturity_date: bond.maturity_date,
                        currency: bond.currency || bond.currency_fixed || 'EUR',
                        volume_issued: bond.volume_as_number,
                        coupon_rate: undefined, // Not available in current data
                        green_bond: bond.tags?.includes('green'),
                        social_bond: bond.tags?.includes('social'),
                        sustainability_bond: bond.tags?.includes('sustainability'),
                        sustainability_linked_bond: bond.tags?.includes('sustainability-linked'),
                        sdg_numbers: bond.sdg_numbers || [],
                        tags: bond.tags || [],
                        public: bond.public
                    }))}
                    loading={loading || isSearching}
                    onTagClick={isAuthenticated ? (tag) => {
                        // Add tag to filters
                        if (tagFilterMode === 'include') {
                            setSelectedTags([...selectedTags, tag]);
                        } else {
                            setExcludedTags([...excludedTags, tag]);
                        }
                    } : undefined}
                    onSDGClick={isAuthenticated ? (sdgNumber) => {
                        // Add SDG to filter
                        const newSDGs = selectedSDGs.includes(sdgNumber)
                            ? selectedSDGs.filter(n => n !== sdgNumber)
                            : [...selectedSDGs, sdgNumber].sort((a, b) => a - b);
                        setSelectedSDGs(newSDGs);
                        updateURLParams({ sdgs: newSDGs, page: 1 });
                        const includeTags = tagFilterMode === 'include' ? selectedTags : [];
                        const excludeTags = tagFilterMode === 'exclude' ? excludedTags : [];
                        if (isInSearchMode && searchQuery) {
                            fetchBonds(1, searchQuery, includeTags, excludeTags, showValidatedOnly, newSDGs);
                        } else {
                            fetchBonds(1, undefined, includeTags, excludeTags, showValidatedOnly, newSDGs);
                        }
                    } : undefined}
                    emptyMessage={isInSearchMode ?
                        t('bonds.noSearchResults', 'No bonds found matching your search.') :
                        t('bonds.noBondsAvailable', 'No bonds available.')
                    }
                    viewMode="grid"
                />
            )}
        </div>
    );
};

export default BondsView;
